import { Card, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Icon, TextField, Typography, Slide, Tooltip } from "@material-ui/core/";
import { ArrowBack, Close, Refresh } from "@material-ui/icons";
import { Services as SdkServices } from "coolremote-sdk";
import { addDays, sub } from "date-fns";
import _, { camelCase, every, findIndex, includes, isEmpty, orderBy } from "lodash";
import moment from "moment-timezone";
import React, { useEffect, useRef, useState } from "react";
import { useHistory, useRouteMatch } from "react-router-dom";
import { t } from "ttag";
import Header from "../../components/Header/Header";
import ServiceNavigationBar from "../../components/Menu/ServiceNavigationBar";
import { useStoreActions, useStoreState } from "../../models/RootStore";
import { MenuSearch as Search } from "../../svgComponents";
import AlertInfo from "./AlertInfo";
import useStyles from "./Alerts.style";
import AlertsTable from "./AlertsTable";
import { omitHiddenColumnFilters } from "./alertsFilters.utils";
import { saveAs } from "file-saver";
import { Button, IconButton, FilterRequire } from '@coolremote/shared';
import CoolDateRangePicker from "../../components/CoolDateRangePicker/CoolDateRangePicker";
import SvgArrow from "../../icons/Arrow";
import clsx from "clsx";
import { AiOutlineCalendar } from "react-icons/ai";
import { TransitionProps } from "@material-ui/core/transitions";
import { Alert } from "@icons";
import useChurnZero from "@hooks/useChurnZero";
import { getUtcRange } from "@services/timeService";


const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const MAX_NOTIFICATION_PARSE_RETRIES = 3;

const DateRangePickerComponent = () => {

  const classes = useStyles();
  const selections: any = useStoreState((s) => s.selections.selections);
  const updateSelections = useStoreActions((a) => a.selections.updateSelections);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [range, setRange] = useState<any>(null);
  const [pickerKey, setKey] = useState<any>(Math.ceil(Math.random() * 1000));
  const [selected, setSelected] = useState<string>(!selections.dateRange ? 'allOpen' : "");

  // ttg doesn't pick-up if it's outside component
  const alertLogsDateRangesPresets = [
    {
      id: 'today',
      name: t`Today`,
      value: {
        startDate: new Date(moment().startOf('day').toISOString()),
        endDate: new Date(moment().endOf('day').toISOString())
      }
    },
    {
      id: 'lastWeek',
      name: t`Last week`,
      value: {
        startDate: new Date(moment().subtract(7, 'days').startOf('day').toISOString()),
        endDate: new Date(moment().endOf('day').toISOString())
      }
    },
    {
      id: 'twoWeeks',
      name: t`Last 2 weeks`,
      value: {
        startDate: new Date(moment().subtract(14, 'days').startOf('day').toISOString()),
        endDate: new Date(moment().endOf('day').toISOString())
      }
    },
    {
      id: 'month',
      name: t`Last month`,
      value: {
        startDate: new Date(moment().subtract(1, 'months').add(1, 'days').startOf('day').toISOString()),
        endDate: new Date(moment().endOf('day').toISOString())
      }
    },
    {
      id: 'allOpen',
      name: t`All open alerts`,
      value: null,
      primary: true
    },
  ];

  const handleNewDateRange = (range: any) => {
    if (range) {
      updateSelections({ type: "time", data: range });
    }
    setIsOpen(false);
  };

  useEffect(() => {
    if (isOpen) {
      return;
    }
    setRange(selections.dateRange);
    setSelected(!selections.dateRange ? 'allOpen' : "");
  }, [isOpen])

  return (
    <>
      <Button
        style={{ width: "20%" }}
        disableRipple
        className={clsx(classes.selectUnitButton)}
        classes={{
          label: classes.selectUnitButton__text,
          endIcon: classes.Arrow
        }}
        size="large"
        onClick={() => setIsOpen(true)}
        endIcon={<SvgArrow />}
      >
        <AiOutlineCalendar style={{ fontSize: '18px', marginRight: '5px' }} />
        <Typography className={classes.datePickerButton}>
          {selections?.dateRange ? `${moment(selections.dateRange.startDate).format("ll")} - ${moment(selections.dateRange.endDate).format("ll")}` : t`All Open Alerts`}
        </Typography>
      </Button>

      <Dialog
        open={isOpen}
        TransitionComponent={Transition}
        keepMounted
        onClose={() => setIsOpen(false)}
      >
        <div className={classes.datePickerDialogContainer}>
          <div className={classes.datePickerDialogHeader}>
            <Typography className={classes.datePickerTitle}>{t`Date Picker`}</Typography>
            <Typography className={classes.datePickerSubtitle}>{t`Select Date Range`}</Typography>
          </div>
          <div
            style={{
              display: 'flex',
            }}
          >
            <div className={classes.datePickerDialogPresetCont} >
              {
                alertLogsDateRangesPresets.map((obj: any) => {
                  return (
                    <Button
                      variant="contained"
                      key={`filterBtn-${obj.id}`}
                      className={clsx(classes.datePickerDialogPresetBtn, obj.id === selected && classes.datePickerDialogPresetBtnActive)}
                      onClick={() => {
                        setRange(obj.value);
                        setSelected(obj.id);
                        setKey((prev: any) => prev + 1);

                        if (!obj.value) {
                          updateSelections({ type: "time", data: null });
                          setIsOpen(false);
                          return;
                        }
                      }}
                    >
                      {obj.name}
                    </Button>
                  )
                })
              }
            </div>
            <div>
              <CoolDateRangePicker
                key={`${pickerKey}`}
                handleSubmit={handleNewDateRange}
                initialRange={range}
                hideIfNull={true}
                // limited to 1 year ago #CS-189
                minDate={new Date(moment().subtract(1, 'year').toISOString())}
                onlyPastAllowed={false}
                noRangeLimit={false}
              />
            </div>
          </div>
        </div>
      </Dialog>
    </>
  )
}

const Alerts: React.FC<any> = (props: any) => {

  const selectedAlertObj = useStoreState((state) => state.selectedAlert);
  const { dateFormat, timeFormat } = useStoreState((state) => state.users);
  const [selectedAlert, setSelectedAlert] = useState<any>(null);
  const [open, setOpen] = useState<boolean>(false);
  const classes = useStyles();
  const types = useStoreState((state) => state.types);
  const allUnits = useStoreState((state) => state.units.allUnits);
  const getAlertsByFilters = useStoreActions((action) => action.alerts.getAlertsByFilters);
  const getNotificationAlerts = useStoreActions((action) => action.alerts.getNotificationAlerts);
  const updateSelections = useStoreActions((a) => a.selections.updateSelections);
  const allAlerts = useStoreState((state) => state.alerts.allAlerts);
  const newerAlertsExist = useStoreState((state) => state.alerts.newerAlertsExist);
  const allSites = useStoreState((state) => state.sites.allSites);
  const getSite = useStoreState((s) => s.sites.getSite);
  const userPreferences = useStoreState((s) => s.users.userPreferences);
  const updateUserPreferences = useStoreActions((a) => a.users.updateUserPreferences);
  const updateAlertStore = useStoreActions((a) => a.alerts.updateAlertStore);
  const getAlertsWithParams = useStoreActions((a) => a.alerts.getAlertsWithParams);
  const setSelectedAlertObj = useStoreActions((actions) => actions.setSelectedAlert);
  const { eventStatusTypes, trapPriorityTypes } = types;
  const sitesFlags = useStoreState((s) => s.sites.sitesFlags);
  const match: any = useRouteMatch();
  const history = useHistory();
  const { siteId } = useStoreState((state) => state.selections.selections);

  const [rows, setRows] = useState<any>([]);
  const [page, setPage] = useState<any>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(userPreferences?.serviceAlertsRowsPerPage || 50);
  const [isDisabled, setIsDisabled] = useState(true);
  const [selectedFilters, setSelectedFilters] = useState<any>(userPreferences?.alertsFilters || { code: [], status: [], type: [], description: [], priority: [], category: [] });
  const [firstload, setFirstLoad] = useState<string>("processing");
  const [openTroubleshoot, setOpenTroubleshoot] = useState<boolean>(false);
  const setUnitUpdateStatus = useStoreActions((action) => action.setUnitUpdateStatus);
  const { addMessage } = useStoreActions((action) => action.errorMessage);
  const selections = useStoreState((s) => s.selections.selections);
  const [resetFilters, setResetFilters] = useState<boolean>(false);
  const [filteredAlerts, setFilteredAlerts] = useState<any>([]);
  const [errorCodeArray, setErrorCodesArray] = useState<any>([]);
  const [descriptionArray, setDescriptionArray] = useState<any>([]);
  const [priorityArray, setPriorityArray] = useState<any>([]);
  const [alertTypeArray, setAlertTypeArray] = useState<any>([]);
  const [statusesArray, setStatusesArray] = useState<any>([]);
  const [categoryArray, setCategoryArray] = useState<any>([]);
  const [file, setFile] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [refresh, setRefresh] = useState<number>(0);
  const [notificationAlertsId, setNotificationAlertsId] = useState<any>(match?.params?.notificationShortId || null);
  const notificationParseRetryRef = useRef({ shortId: null as string | null, count: 0 });
  const isFilterSelected = !every(selectedFilters, isEmpty);
  const eventCategoriesData = useStoreState(
    (state) => state.types.eventCategoriesData
  );

  const { trackEvent } = useChurnZero()

  const { unitTypes } = types;

  useEffect(() => {
    setUnitUpdateStatus({ status: "" });
  }, []);

  useEffect(() => {
    if (!resetFilters) {
      setResetFilters(true);
      setFirstLoad("init");
    }
  }, [resetFilters]);

  // Handle URL parameter changes to update notificationAlertsId
  useEffect(() => {
    const shortId = match?.params?.notificationShortId || null;
    if (shortId !== notificationAlertsId) {
      setNotificationAlertsId(shortId);
    }
  }, [match?.params?.notificationShortId]);

  // Helper function to populate filter arrays from alerts data
  const populateFilterArrays = (alerts: any[]) => {
    const errorCodesArray: any[] = [];
    const alertTypeArray: any[] = [];
    const descriptionArray: any[] = [];
    const priorityArray: any[] = [];
    const statusesArray: any[] = [];
    const categoryArray: any[] = [];

    alerts.forEach((alert: any) => {
      if (alert.errorCode && !errorCodesArray.includes(alert.errorCode)) {
        errorCodesArray.push(alert.errorCode);
      }
      if (alert.alertType && !alertTypeArray.includes(alert.alertType)) {
        alertTypeArray.push(alert.alertType);
      }
      if (alert.description && !descriptionArray.includes(alert.description)) {
        descriptionArray.push(alert.description);
      }
      if (alert.priority && !priorityArray.includes(alert.priority)) {
        priorityArray.push(alert.priority);
      }
      if (alert.status && !statusesArray.includes(alert.status)) {
        statusesArray.push(alert.status);
      }
      if (alert.category && !categoryArray.includes(alert.category)) {
        categoryArray.push(alert.category);
      }
    });

    setErrorCodesArray(errorCodesArray.sort());
    setStatusesArray(statusesArray.sort());
    setAlertTypeArray(alertTypeArray.sort());
    setDescriptionArray(descriptionArray.sort());
    setPriorityArray(priorityArray.sort());
    setCategoryArray(categoryArray.sort());
  };

  const getAlertContainerIds = (alert: any) => ({
    siteId:
      alert?.alertItemContainerIds?.siteId ||
      alert?.siteId ||
      alert?.site?.id ||
      alert?.containerIds?.siteId ||
      null,
    systemId:
      alert?.alertItemContainerIds?.systemId ||
      alert?.systemId ||
      alert?.system?.id ||
      alert?.containerIds?.systemId ||
      null,
    unitId:
      alert?.alertItemContainerIds?.unitId ||
      alert?.unitId ||
      alert?.unit?.id ||
      alert?.containerIds?.unitId ||
      null,
  });

  const getAlertsScopeMatch = (alertsData: any[]) => {
    if (!alertsData?.length) {
      return {
        commonSiteId: null,
        commonSystemId: null,
        commonUnitId: null,
      };
    }

    const firstAlertIds = getAlertContainerIds(alertsData[0]);

    const sameSite = !!firstAlertIds.siteId && alertsData.every((alert: any) => getAlertContainerIds(alert).siteId === firstAlertIds.siteId);
    const sameSystem = !!firstAlertIds.systemId && alertsData.every((alert: any) => getAlertContainerIds(alert).systemId === firstAlertIds.systemId);
    const sameUnit = !!firstAlertIds.unitId && alertsData.every((alert: any) => getAlertContainerIds(alert).unitId === firstAlertIds.unitId);

    return {
      commonSiteId: sameSite ? firstAlertIds.siteId : null,
      commonSystemId: sameSystem ? firstAlertIds.systemId : null,
      commonUnitId: sameUnit ? firstAlertIds.unitId : null,
    };
  };

  useEffect(() => {
    if (match?.params?.siteId && match?.params?.filterType) {
      const site = allSites[match.params.siteId];
      const filter = eventCategoriesData[match.params.filterType];
      if (site) {
        updateSelections({ type: "site", data: match.params.siteId });
      }
      if (filter) {
        clearFilters();
        setSelectedFilters((prev: any) => ({
          ...prev,
          category: [filter.name],
        }));
      }
    }
  }, [match.params.filterType, match.params.siteId]);

  // Separate effect to handle site selection from notification alerts
  useEffect(() => {

    if (notificationAlertsId && rows.length > 0 && !siteId) {
      const firstAlert = rows[0] as any;
      const alertSiteId = firstAlert.alertItemContainerIds?.siteId ||
        firstAlert.siteId ||
        firstAlert.site?.id ||
        firstAlert.containerIds?.siteId;

      if (alertSiteId) {
        updateSelections({ type: "site", data: alertSiteId });
      }
    }
  }, [rows, notificationAlertsId, siteId]);

  useEffect(() => {
    if (selectedAlertObj && selectedAlertObj.id) {
      const {
        timestamp,
        unitId,
        systemId,
        siteId,
        isSensor,
        isPowerMeter
      } = selectedAlertObj;
      const evDate = new Date(timestamp);
      const endDate = moment(evDate).format("YYYY-MM-DD") !== moment().format("YYYY-MM-DD") ? addDays(evDate, 1) : evDate;
      const startDate = sub(evDate, { hours: 24 });
      updateSelections({ type: "time", data: { startDate, endDate } });
      if (!isSensor && !isPowerMeter) {
        if (unitId) {
          updateSelections({ type: "unit", data: unitId });
        }
        else if (systemId) {
          updateSelections({ type: "system", data: systemId });
        }
        else if (siteId) {
          updateSelections({ type: "site", data: siteId });
        }
      } else {
        if (siteId) {
          updateSelections({ type: "site", data: siteId });
        }
      }

      return;
    }
    if (!selections?.dateRange?.endDate || !selections?.dateRange?.startDate) {
      updateSelections({
        type: "time",
        data: null
      });
    }

  }, [allAlerts, selectedAlertObj]);

  useEffect(() => {

    if (match?.params?.notificationShortId) {
      const shortId = match?.params?.notificationShortId;
      clearFilters();
      getNotificationAlerts({ notificationShortId: shortId })
        .then((resp: any) => {
          const { parsed, rawAlerts } = resp;
          setNotificationAlertsId(shortId);
          const alertsData = Object.values(parsed);
          const rawAlertValues = Object.values(rawAlerts) as any[];

          // No alert returned at all (invalid shortId)
          if (rawAlertValues.length === 0) {
            notificationParseRetryRef.current = { shortId: null, count: 0 };
            setNotificationAlertsId(null);
            setFirstLoad("done");
            return;
          }

          // Use raw alert data to switch to the correct customer/site context.
          // This works even when parseAlerts can't resolve the alert (e.g. units not
          // yet loaded in performance_v1 mode).
          const firstRaw = rawAlertValues[0] as any;
          const rawSiteId = firstRaw.site;
          if (rawSiteId && rawSiteId !== siteId) {
            updateSelections({ type: "site", data: rawSiteId });
          }

          if (alertsData.length > 0) {
            notificationParseRetryRef.current = { shortId: null, count: 0 };
            const { commonSiteId, commonSystemId, commonUnitId } = getAlertsScopeMatch(alertsData);

            // Only update site if parsed data points somewhere different than raw
            if (commonSiteId && commonSiteId !== rawSiteId && commonSiteId !== siteId) {
              updateSelections({ type: "site", data: commonSiteId });
            }
            if (commonSystemId && commonSystemId !== selections.systemId) {
              setTimeout(() => updateSelections({ type: "system", data: commonSystemId }), 100);
            }
            if (commonUnitId && commonUnitId !== selections.unitId) {
              setTimeout(() => updateSelections({ type: "unit", data: commonUnitId }), 200);
            }

            setRows(alertsData);
            populateFilterArrays(alertsData);
          } else {
            // parseAlerts returned empty — units for this site are not in the store yet
            // (performance_v1 loads them lazily after the site switch above).
            // Schedule a re-fetch so the alert displays once units are available.
            setRows([]);
            populateFilterArrays([]);
            if (notificationParseRetryRef.current.shortId !== shortId) {
              notificationParseRetryRef.current = { shortId, count: 0 };
            }
            if (notificationParseRetryRef.current.count < MAX_NOTIFICATION_PARSE_RETRIES) {
              notificationParseRetryRef.current.count += 1;
              setTimeout(() => setRefresh((r: number) => r + 1), 1500);
            }
          }

          setFirstLoad("done");
        })
        .catch((err: any) => {
          notificationParseRetryRef.current = { shortId: null, count: 0 };
          addMessage({ message: err.message });
          setNotificationAlertsId(null);
          setFirstLoad("done");
        })
        .finally(() => setIsDisabled(false));
      return;
    }

    let startTime = 0;
    let endTime = 0;
    if (selections.dateRange) {
      const timezone = getSite(selections?.siteId)?.timezone || moment.tz.guess();
      // Calculate UTC start and end dates
      const range = getUtcRange(
        selections.dateRange?.startDate,
        selections.dateRange?.endDate,
        timezone
      );
      startTime = range.startTime;
      endTime = range.endTime;
    }

    let cancelled = false;

    (async () => {
      setIsDisabled(true);
      setRows([]);
      firstload === "init" && setFirstLoad("processing");
      const alertsAPIresult = !selections.dateRange ?
        await getAlertsByFilters({
          status: [types.eventStatusTypes["open"], types.eventStatusTypes["acknowledged"]],
          openAlertsOnly: false
        }) :
        await getAlertsByFilters({
          startTime,
          endTime,
          withAllOpenAlerts: false
        });

      // Discard the response if a newer request started while this one was in flight
      if (cancelled) {
        return;
      }

      const sortedAlerts = orderBy(alertsAPIresult, ["eventTime"], ["desc"]);
      firstload !== "done" && setFirstLoad("done");

      // Populate filter arrays for regular alerts
      populateFilterArrays(sortedAlerts);

      setRows(sortedAlerts);
      setOpen(true);
      setIsDisabled(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [siteId, selections.dateRange, refresh, timeFormat, dateFormat, match.params.notificationShortId, userPreferences.alertLogUnitDisplay]);

  useEffect(() => {
    if (!rows.length) {
      setFilteredAlerts([]);
      return;
    }

    const filteredAlerts = getFilteredAlerts(rows);
    setFilteredAlerts(filteredAlerts);
  }, [selections, selectedFilters, rows, searchTerm, notificationAlertsId]);

  useEffect(() => {
    setPage(0);
  }, [selections, selectedFilters, searchTerm, notificationAlertsId]);

  useEffect(() => {
    setSelectedAlert(null);
  }, [selections.siteId, selections.customerId]);

  useEffect(() => {
    const cleanedFilters = omitHiddenColumnFilters(selectedFilters, userPreferences?.AlertLogColumnPref)
    if (cleanedFilters !== selectedFilters) {
      setSelectedFilters(cleanedFilters)
    }
  }, [selectedFilters, userPreferences?.AlertLogColumnPref]);

  useEffect(() => {
    if (JSON.stringify(userPreferences?.alertsFilters) !== JSON.stringify(selectedFilters)) {
      updateUserPreferences({ alertsFilters: selectedFilters })
    }
  }, [selectedFilters]);

  useEffect(() => {
    if (!isEmpty(selectedAlertObj) && !isEmpty(filteredAlerts)) {

      const alertIndex = findIndex(filteredAlerts, { id: selectedAlertObj.id });
      const pageNum = alertIndex === -1 ? 1 : Math.ceil((alertIndex + 1) / rowsPerPage);
      setSelectedAlert(filteredAlerts[alertIndex]);
      setPage(pageNum - 1);
    }
  }, [filteredAlerts, rowsPerPage]);

  useEffect(() => {
    if (selectedAlert) {
      const updated = filteredAlerts.find((a: any) => a.id === selectedAlert.id);
      if (updated) {
        setSelectedAlert(updated);
      }
    }
  }, [filteredAlerts]);

  const clearFilters = () => {
    setSelectedFilters({
      code: [],
      status: [],
      type: [],
      description: [],
      priority: [],
      category: [],
    });
  };

  const closeTroubleshootFile = () => {
    setOpenTroubleshoot(false);
    setFile(null);
  };
  const clearAllFilters = () => {
    setSelectedFilters({ code: [], status: [], type: [], description: [], priority: [], category: [] });
  };
  const selectUnit = (unitId: string) => {
    updateSelections({ type: "unit", data: unitId });
  },
    onRowSelect = (alert: any) => {
      setSelectedAlert(alert);
      setOpen(true);
    },
    setPageNum = (num: number) => {
      setPage(num);
    },
    setNumPerPage = (num: number) => {
      setRowsPerPage(num);
      updateUserPreferences({ serviceAlertsRowsPerPage: num });
    },
    updateFilters = (newFilters: any) => {
      setSelectedFilters({ ...selectedFilters, ...newFilters });
      setPage(0);
    },
    refreshAlerts = () => {
      setRefresh(refresh + 1);
      getAlertsWithParams({ status: [types.eventStatusTypes["open"], types.eventStatusTypes["acknowledged"]] });
    },
    addResolvedDescription = (resolvedDescription: string, id: string) => {
      let selectedAlert = null;
      const changedAlerts = filteredAlerts.map((alert: any) => {
        if (alert.id === id) {
          selectedAlert = { ...alert, userText: resolvedDescription };
          return selectedAlert;
        }
        return alert;
      });

      setFilteredAlerts(getFilteredAlerts(changedAlerts));
      setSelectedAlert(selectedAlert);
    },
    updateAlertLocally = (alertObj: any, data: any) => {
      const isSnoozeUpdate = "snoozeUsername" in data;
      const scope = data.snoozeScope || alertObj.snoozeScope;
      const targetSystemId = data.snoozeSystemId || alertObj.snoozeSystemId || alertObj.systemId || alertObj.alertItemContainerIds?.systemId;
      const targetUnitId = data.snoozeUnitId || alertObj.snoozeUnitId || alertObj.unitId || alertObj.alertItemContainerIds?.unitId;

      const matchesSnoozeTarget = (a: any) => {
        if (!isSnoozeUpdate) {
          return a.id === alertObj.id;
        }
        if (scope === "system" && targetSystemId) {
          const aSysId = a.systemId || a.alertItemContainerIds?.systemId;
          const aSysIds = a.systemIds || a.alertItemContainerIds?.systemIds || [];
          return aSysId === targetSystemId || aSysIds.includes(targetSystemId);
        }
        if (scope === "unit" && targetUnitId) {
          const aUnitId = a.unitId || a.alertItemContainerIds?.unitId;
          const aUnitIds = a.unitIds || a.alertItemContainerIds?.unitIds || [];
          return aUnitId === targetUnitId || aUnitIds.includes(targetUnitId);
        }
        return a.id === alertObj.id;
      };

      const updatedRows = rows.map((alert: any) => {
        if (matchesSnoozeTarget(alert)) {
          return { ...alert, ...data };
        }
        return alert;
      });
      setRows(updatedRows);

      let selectedAlert = null;
      let selectedAlertIndex = -1;
      const updatedAlerts = filteredAlerts.map((alert: any, i: number) => {
        if (matchesSnoozeTarget(alert)) {
          const updated = { ...alert, ...data };
          if (alert.id === alertObj.id) {
            selectedAlert = updated;
            selectedAlertIndex = i;
          }
          return updated;
        }
        return alert;
      });

      if (data.status) {
        updateAlertStore({ id: alertObj.id, data: { status: eventStatusTypes[camelCase(data.status)] } });
      }

      let newFilteredAlerts;
      if (isSnoozeUpdate) {
        newFilteredAlerts = getFilteredAlerts(updatedAlerts);
      } else {
        newFilteredAlerts = getFilteredAlerts(updatedAlerts.filter((_: any, i: number) => i !== selectedAlertIndex));
      }

      setFilteredAlerts(newFilteredAlerts);
      setSelectedAlert(selectedAlert || newFilteredAlerts?.[0]);
    },
    convertDataToCSV = () => {

      trackEvent('ExportAlert', 'The user exported an alert report');

      let csvContent = "\uFEFF"; // UTF-16 BOM (Byte Order Mark)

      const headers = [
        "DATE & TIME",
        "CLOSE DATE & TIME",
        "UNIT NAME",
        "SITE NAME",
        "SYSTEM NAME",
        "ALERT ID",
        "DESCRIPTION",
        "ALERT STATUS",
        "ERROR CODE",
        "ERROR DESCRIPTION",
        "ALERT TYPE",
        "PRIORITY"
      ];

      csvContent += headers.join(",") + "\n";

      for (const row of filteredAlerts) {
        const alertId = (row.shortId || "").replace(/(\r\n|\n|\r|,)/gm, " ");
        const description = (row.description || "").replace(/(\r\n|\n|\r|,)/gm, " ");
        const isAnomalyAlert = row.severity?.id === "ANOMALIES";
        const unitName = (
          (isAnomalyAlert && row.unitNamesTooltip) || row.unitName || ""
        ).replace(/(\r\n|\n|\r)/gm, " ");
        const siteName = (row.siteName || "").replace(/(\r\n|\n|\r|,)/gm, " ");
        const systemName = (row.systemName || "").replace(/(\r\n|\n|\r|,)/gm, " ");
        const errorDescription = (row.errorDescription || "").replace(/(\r\n|\n|\r|,)/gm, " ");

        const customRow = [
          row.time,
          row.status !== "Open" ? row.clearTime || "" : "",
          unitName,
          siteName,
          systemName,
          alertId,
          description,
          row.status,
          row.errorCode,
          errorDescription,
          row.alertType,
          trapPriorityTypes[row?.priority] || "-"
        ];
        csvContent += customRow.map(value => value && `"${value?.replace(/"/g, '""')}"`).join(",") + "\n";
      }

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-16" });
      saveAs(blob, "Alerts Log.csv");
    };

  const exitNotificationMode = () => {
    notificationParseRetryRef.current = { shortId: null, count: 0 };
    setNotificationAlertsId(null);
    setSelectedAlert(null);
    setSelectedAlertObj(null);
    setSearchTerm("");
    clearFilters();
    history.push("/alerts");
  };

  const showAllAlerts = () => {
    exitNotificationMode();
    updateSelections({ type: "system", data: null });
  };

  const handleSiteSelect = () => {
    if (notificationAlertsId) {
      exitNotificationMode();
    }
  };

  // Apply filters and selections to alerts
  const getFilteredAlerts = (alerts: any[]) => {
    function applyFilters(alerts: any[]) {
      return _(alerts)
        .filter((alert) => {
          return selectedFilters.code.length
            ? selectedFilters.code.includes(alert.errorCode || "")
            : true;
        })
        .filter((alert) => {
          return selectedFilters.type.length
            ? selectedFilters.type.includes(alert.alertType)
            : true;
        })
        .filter((alert) => {
          return selectedFilters.status.length
            ? selectedFilters.status.includes(alert.status)
            : true;
        })
        .filter((alert) => {
          return selectedFilters.description.length
            ? selectedFilters.description.includes(alert.description)
            : true;
        })
        .filter((alert) => {
          return selectedFilters.priority.length
            ? selectedFilters.priority.includes(trapPriorityTypes[alert.priority])
            : true;
        })
        .filter((alert) => {
          return selectedFilters.category.length
            ? selectedFilters.category.includes(alert.severity.name)
            : true;
        })
        .filter((alert) => {
          return searchTerm?.length
            ? (
              alert?.description?.toUpperCase()?.includes(searchTerm?.toUpperCase())
              || alert?.shortId?.toUpperCase()?.includes(searchTerm?.toUpperCase())
              || alert?.errorCode?.toUpperCase()?.includes(searchTerm?.toUpperCase())
            )
            : true;
        })
        .value();
    }
    function applySelections(alerts: any[]) {
      return _(alerts)
        .filter((alert: any) =>
          selections.unitId ? alert.alertItemContainerIds.unitId
            ? alert.alertItemContainerIds.unitId === selections.unitId || allUnits[alert.alertItemContainerIds.unitId]?.serviceUnits?.includes(selections.unitId)
            : (alert.alertItemContainerIds.unitIds.length > 1 && alert.alertItemContainerIds.unitIds.includes(selections.unitId))
            : true
        )
        .filter((alert: any) =>
          selections.siteId
            ? alert.alertItemContainerIds.siteId === selections.siteId
            : true
        )
        .filter((alert: any) =>
          selections.systemId && !selections.unitId ? alert.alertItemContainerIds.systemId
            ? alert.alertItemContainerIds.systemId === selections.systemId
            : (alert.alertItemContainerIds.systemIds.length > 1 && alert.alertItemContainerIds.systemIds.includes(selections.systemId))
            : true
        )
        .filter((alert: any) =>
          selections.systemId && includes(selections.systemId, "_")
            ? allUnits[alert.alertItemContainerIds.unitId].line.toString() === selections.systemId.split("_")[0] &&
            allUnits[alert.alertItemContainerIds.unitId].device === selections.systemId.split("_")[1]
            : true
        )
        .filter((alert: any) =>
          selections.customerId
            ? alert.alertItemContainerIds.customerId === selections.customerId
            : true
        )
        .orderBy(["timestamp"], ["desc"])
        .value();
    }

    function onlyUnique(value: any, index: any, self: any) {
      return self.indexOf(value) === index;
    }

    const updateFiltersVals = (rows: any) => {
      const errorCodeArr: any = [];
      const statusArr: any = [];
      const alertTypesArr: any = [];
      const priorityArr: any = [];
      const descriptionArr: any = [];
      const categoryArr: any = [];
      let blankAdded: any = false;
      for (let rowIndex in rows) {
        const { status, errorCode, alertType, description, priority, severity } = rows[rowIndex];
        if (!errorCode && blankAdded === false) {
          errorCodeArr.push("");
          blankAdded = true;
        }
        if (!!errorCode) { errorCodeArr.push(errorCode); }
        if (!!status) { statusArr.push(status); }
        if (!!alertType) { alertTypesArr.push(alertType); }
        if (!!priority) { priorityArr.push(trapPriorityTypes[priority]); }
        if (!!description) { descriptionArr.push(description); }
        if (!!severity) { categoryArr.push(severity.name); }
      }

      setErrorCodesArray(errorCodeArr.filter(onlyUnique));
      setStatusesArray(statusArr.filter(onlyUnique));
      setAlertTypeArray(alertTypesArr.filter(onlyUnique));
      setPriorityArray(priorityArr.filter(onlyUnique));
      setDescriptionArray(descriptionArr.filter(onlyUnique));
      setCategoryArray(categoryArr.filter(onlyUnique));
    };
    const alertsBasedTopSelections = applySelections(alerts)
    const filteredRows = applyFilters(alertsBasedTopSelections);
    updateFiltersVals(alertsBasedTopSelections);
    return filteredRows;
  };

  const generateReport = (templateId: string, unitId: string) => {
    SdkServices.generateTroubleshootReport(templateId, unitId)
      .then((report: any) => {
        setFile(report);
      })
      .catch((err: any) => addMessage({ message: err.message }));
  };

  const getMoreInfo = (alert: any) => {
    if (!alert?.unitIds?.length || alert.isSensor || alert.isPowerMeter || alert.disableDoubleClick) {
      return;
    }

    let selectedUnit = alert.unitIds[0];

    if (alert?.unitIds?.length > 0) {
      const unit = allUnits[alert.unitIds[0]] || {};
      if (!unit) {
        return;
      }
      const { site = "", type } = unit;
      const { isServiceSite } = sitesFlags[site];
      const isAnomaly = alert?.alertType === "Anomaly";
      if (isAnomaly) {
        const unitCounts = alert.unitIds.reduce((data: any, unitId: any) => {
          const unit = allUnits[unitId];
          if (unit.type === unitTypes["service"]) {
            data.indoorCount++;
          }
          if (unit.type === unitTypes["outdoor"]) {
            data.outdoorCount++;
            if (!data.firstODU) {
              data.firstODU = unit.id;
            }
          }
          return data;
        }, { indoorCount: 0, outdoorCount: 0, firstODU: null });

        const isMixed = unitCounts.indoorCount > 0 && unitCounts.outdoorCount > 0;

        if (isMixed) {
          selectedUnit = unitCounts?.firstODU || alert.unitIds[0];
        }
      }

    }

    localStorage.setItem("time", alert.timestamp);
    localStorage.setItem("alertCheck", JSON.stringify(true));
    localStorage.setItem("unitId", selectedUnit);
    alert.trap && localStorage.setItem("trap", alert.trap);
    alert.ruleId ? localStorage.setItem("ruleId", alert.ruleId) : localStorage.removeItem("ruleId");
    // history.push(`/unit-stats/${selectedUnit}`);
    window.open(`${window.location.origin}/unit-stats/${selectedUnit}`, '_blank');

  };


  const searchComponent = (
    <TextField
      placeholder={t`Search...`}
      value={searchTerm}
      onChange={(event: any) => setSearchTerm(event.target.value)}
      InputProps={{
        disableUnderline: true, classes: { root: classes.inputRoot },
        endAdornment:
          !searchTerm ? (<Search />) : (
            <IconButton
              onClick={() => setSearchTerm("")}
              className={classes.closeIconStyle}
            >
              <Close />
            </IconButton>
          )
      }}
    />
  );

  return (
    <div className={classes.view}>
      <ServiceNavigationBar {...props} />
      <div className={classes.contentArea}>
        <Header
          showDateRangePicker={true}
          hideUnitSelection={false}
          hideSystemSelection={false}
          hideSiteSelection={false}
          hideCustomerSelection={!!notificationAlertsId}
          allowUnassignedControls={true}
          searchComponent={searchComponent}
          textAllCustomers={true}
          applySiteTypeFiltering
          screenTitle="alerts"
          onSiteSelect={handleSiteSelect}
          customDateRangePickerComponent={<DateRangePickerComponent />}
        />
        {
          !siteId ? (
            <FilterRequire type={'site'} />
          ) : (
            <>
              <Grid className={classes.contentHeaderContainer}>
                {newerAlertsExist && <div className={classes.newAlertsContainer}>
                  <Alert className={classes.alertIcon} />
                  <div className={classes.alertsMessagesContainer}>
                    <Typography className={classes.alertMessage}>{t`New alerts available. Click Refresh button to update page.`}</Typography>
                    {isFilterSelected && <Typography className={classes.alertMessage}>{t`Some filters are applied on the page and may hide the new alerts`}</Typography>}
                  </div>
                </div>}
                <div className={classes.buttonsContainer}>
                  {
                    !!notificationAlertsId &&
                    <Button
                      disableRipple
                      variant="outlined"
                      startIcon={
                        <ArrowBack />
                      }
                      onMouseUp={showAllAlerts}
                    >
                      {t`Show all alerts`}
                    </Button>
                  }
                  {
                    !notificationAlertsId &&
                    <Button
                      disableRipple
                      variant="contained"
                      className={classes.shareButton}
                      startIcon={
                        <Icon style={{ transform: "rotateY(180deg)", marginTop: "-3px" }}>reply</Icon>
                      }
                      onMouseUp={convertDataToCSV}
                    >
                      {t`Export Report`}
                    </Button>
                  }
                </div>
              </Grid>
              <Grid container className={classes.contentContainer}        >
                <Grid item className={classes.alertsTable}>
                  <Card className={classes.content}>
                    <AlertsTable
                      rows={filteredAlerts}
                      refreshAlerts={refreshAlerts}
                      onRowSelect={onRowSelect}
                      setPage={setPageNum}
                      page={page}
                      setRowsPerPage={setNumPerPage}
                      rowsPerPage={rowsPerPage}
                      isDisabled={isDisabled}
                      selectedAlert={selectedAlert && selectedAlert.id}
                      setSelectedAlert={setSelectedAlert}
                      filterValues={{
                        code: errorCodeArray,
                        status: statusesArray,
                        type: alertTypeArray,
                        description: descriptionArray,
                        priority: priorityArray,
                        category: categoryArray
                      }}
                      appliedFilters={selectedFilters}
                      getSelectedFilters={updateFilters}
                      style={{ height: "100%" }}
                      clearAllFilters={clearAllFilters}
                      getMoreInfo={getMoreInfo}
                    />
                  </Card>
                </Grid>

                <Grid item className={classes.alertInfoContainer}>
                  <Card className={classes.content}>
                    {selectedAlert && <AlertInfo
                      open={!!(open && rows.length)}
                      alert={selectedAlert}
                      selectUnit={selectUnit}
                      refreshAlerts={refreshAlerts}
                      addResolvedDescription={addResolvedDescription}
                      updateAlertLocally={updateAlertLocally}
                      setOpenTroubleshoot={setOpenTroubleshoot}
                      generateReport={generateReport}
                      getMoreInfo={getMoreInfo}
                      allSites={allSites}
                    />}
                  </Card>
                </Grid>
              </Grid>
            </>
          )
        }
      </div>

      {
        openTroubleshoot &&
        <Dialog onClose={closeTroubleshootFile} aria-labelledby="customized-dialog-title" open={openTroubleshoot}>
          <DialogTitle id="customized-dialog-title">
            {t`Troubleshoot guidance`}
          </DialogTitle>
          <DialogContent dividers>
            {!!file ? <div dangerouslySetInnerHTML={{ __html: file }} /> : <span />}
          </DialogContent>
          <DialogActions>
            <Button autoFocus onClick={closeTroubleshootFile}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      }
    </div>
  );
};
export default Alerts;
